import RemoteResourceRepository from './RemoteResourceRepository'

import moment from 'moment'
import {isNil, omit, omitBy, range, flatten} from 'lodash'
import {DEFAULT_BULK_SIZE} from '../constants'

/**
 * Private methods declarations
*/
const mapNoteKeysToRemote = Symbol('mapNoteKeysToRemote')
const mapFiltersToRemote = Symbol('mapFiltersToRemote')

/**
 * RemoteNotesRepository expands the implementation of the RemoteRepository,
 * adding Notes specific query parameters
*/
export default class RemoteNoteRepository extends RemoteResourceRepository {
  constructor () {
    super('note')
  }

  /**
   * Maps notes key to a format expected by the BriteNotes API
   * @param {String} key to be mapped
   * @returns {String} the mapped key
  */
  [mapNoteKeysToRemote] (key) {
    const map = {
      'reference.reference_label': 'reference_label',
      'creator.name': 'creator_name'
    }

    return map[key] || key
  }

  /**
   * Maps note filter parameters to match the ones used by the BriteNotes API
   * @param {Object} filters from the UI
   * @returns {Object} mapped filters
  */
  [mapFiltersToRemote] (filters) {
    let mappedFilters = omit(filters, 'date_added')
    const dateAdded = filters.date_added
    if (dateAdded) {
      const fmtDate = d => moment(d).format('YYYY-MM-DD')
      mappedFilters.date_added_since = fmtDate(dateAdded[0])
      mappedFilters.date_added_until = fmtDate(dateAdded[1])
    }
    if (filters.tags) {
      mappedFilters.tags = filters.tags.join(',')
    }
    mappedFilters = omitBy(mappedFilters, (value, key) =>
      value === '' || isNil(value)
    )
    const jsonApiFilters = {}
    Object.keys(mappedFilters).map(key => {
      jsonApiFilters[`filter[${key}]`] = mappedFilters[key]
    })
    return jsonApiFilters
  }

  /**
   * We are extending the "listRecords" method here to map some fields
   * that are specific to the BriteNotes API implementation
   * reference label: on the frontend codebase, we are using the term "reference.label"
   * so that we can easily provide it to components that use lodash's get syntax
   * contents: the keyword used for searching is called contents on the API, on the
   * frontend the keyword is "search"
   */
  listRecords ({queries = null}) {
    if (queries) {
      if (queries.sort) {
        const sort = queries.sort
        sort.key = this[mapNoteKeysToRemote](sort.key)
      }
      if (queries.search) {
        if (!queries.filters) queries.filters = {}
        queries.filters.contents = queries.search
      }
      if (queries.filters) {
        queries.filters = this[mapFiltersToRemote](queries.filters)
      }
      delete queries.search
    }

    return super.listRecords({queries})
  }

  /**
   * Returns a list of note history events
   * @param {uuid} note uuid
   * @returns {Array<Object>} list of history events
  */
  async listAllHistoryRecords ({id}) {
    const response = await this.http.one('note', id).all('history').get({
      page: {
        number: 1,
        size: DEFAULT_BULK_SIZE
      }
    })
    const {data, errors, meta, links} = response

    // 2. Populate remaining requests
    // - Return if errors are found.
    if (errors) {
      return {errors}
    }
    // - Short-circuit exit if no further fetching is needed.
    if (meta.pagination.pages === 1) {
      return {data, errors, meta, links}
    }
    // - Populate the remaining requests
    const requests = range(2, meta.pagination.pages + 1).map(page => {
      return this.http.one('note', id).all('history').get({
        page: {
          number: page,
          size: DEFAULT_BULK_SIZE
        }
      })
    })
    const responses = await Promise.all(requests)
    // 3. Return aggregated data
    return {
      data: [
        response.data,
        ...flatten(responses.map(response => response.data))
      ]
    }
  }
}
